import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import showToast from "../../utils/toastNotifications";

import ProgressIndicator from "../../components/Common/ProgressIndicator";
import CustomButton from "../../components/Form/CustomButton";

import DeliveryDetails from "../../components/Client/OrderDetail/DeliveryDetailsCard";
import OfferDetails from "../../components/Client/OrderDetail/OfferDetailsCard";
import RevisionDetails from "../../components/Client/OrderDetail/RevisionDetailsCard";
import ResolveDetails from "../../components/Client/OrderDetail/ResolveDetailsCard";
import ComplainDetails from "../../components/Client/OrderDetail/ComplainDetailsCard";

import ReviewOrderModel from "../../components/Model/ReviewOrderModel";
import {
  orderStatus,
  orderStatusDescription,
  roles,
} from "../../constants/commonConstants";
import { useConfirmation } from "../../hooks/useConfirmation";
import { ConfirmationModal } from "../../components/Model/ConfirmationModel";

import { useThunk } from "../../hooks/useThunk";
import { acceptOrder, getUserOrderById } from "../../store";
import { addOrderReview } from "../../store/thunks/orderThunks";
import { overRideStatus } from "../../utils/statusUtils";
import { currencyFormatter } from "../../utils/formatting";
import { formatYYYY_MM_DD } from "../../utils/dateFormating";
import { isValidObjectId } from "../../utils/validationUtils";
import ErrorDisplay from "../../error/ErrorDisplay";
import OrderDetailSkeleton from "../../components/skeleton/OrderDetailSkeleton";

const OrderDetails = () => {
  
  const navigate = useNavigate();
  const { requestConfirmation, confirmation, confirm, cancel } =
    useConfirmation();

  const authData = JSON.parse(localStorage.getItem("auth") || null);

  const { orderId } = useParams();
  const jobId = orderId;

  const [isOpenReviewOrderModel, setIsOpenReviewOrderModel] = useState(false);

  // Note: Fetch order By orderId
  const [doGetUserOrderByID, isGetUserOrderByID, getUserOrderByIDError] =
    useThunk(getUserOrderById);
  // Note: acceptOrder
  const [doAcceptOrder, isAcceptOrder, acceptOrderError] =
    useThunk(acceptOrder); 
  // Note: addReview
  const [doAddOrderReview, isAddOrderReview, addOrderReviewError] =
    useThunk(addOrderReview);
  
  const [orderData, setOrderData] = useState();
  const [isloading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [jobId]);

  const handleAccept = async () => {
    const isConfirmed = await requestConfirmation({
      type: "accept",
      title: "Accept Order",
      message:
        "Are you sure you want to accept this order? Once accepted, no further revisions can be requested.",
      confirmText: "Accept Now",
      cancelText: "Cancel",
    });

    if (isConfirmed) {
      setIsLoading(true);
      await handleAcceptOrder();
      setIsOpenReviewOrderModel(true);
    }
  };

  const fetchOrder = async () => {
    const userOrder = await doGetUserOrderByID(jobId);

    const data = userOrder?.response?.data;
    console.log("fetched order details", data);

    if (userOrder?.success) {
      setOrderData(data);
    }
  };

  const handleAcceptOrder = async () => {
    const acceptedOrder = await doAcceptOrder({ orderId: jobId });

    const data = acceptedOrder?.response?.data;

    if (acceptedOrder?.success) {
      showToast("success", "Order Accepted Successfully");
      setOrderData(data);
    } else {
      showToast("error", acceptedOrder?.error?.message || "Failed to accept");
    }
  };

  const handleOrderReviewSubmit = async (data) => {
    const payload = {
      orderId: orderId,
      rating: data.rating,
      feedback: data.feedBack,
    };
    const reviewedOrder = await doAddOrderReview(payload);

    if (reviewedOrder?.success) {
      showToast("success", "Review Submitted Successfully");
      setIsOpenReviewOrderModel(false);
      fetchOrder();
    }
    else {
      showToast("error", reviewedOrder?.error?.message || "Failed to submit review");
      setIsOpenReviewOrderModel(false);
    }
  };

  const checkStatus = (status) => {
    return orderData?.orderProgress?.some(
      (item) => item.progressStatus === status
    );
  };

  return (
    <div className="mt-12 px-5 sm:px-10 lg:px-[6.25rem] mb-[5.5rem] min-w-[320px]">
      {
        // Show ErrorDisplay if orderId is invalid or there is an error
        !isValidObjectId(orderId) || getUserOrderByIDError ? (
          <ErrorDisplay
            errorMessage={`${
              getUserOrderByIDError?.message || "Invalid Order ID"
            }`}
            buttonText="Back to Order History"
            onButtonClick={() => navigate("/client/order-history")}
          />
        ) : isGetUserOrderByID ? (
          // Show OrderDetailSkeleton if fetching order details
          <OrderDetailSkeleton />
        ) : (
          <div className="flex flex-col lg:flex-row lg:gap-[2.875rem]">
            {/* Main Content */}
            <div className="flex flex-col flex-1 gap-3 h-fit">
              <div className="flex flex-col space-x-2 space-y-2 md:flex-row md:space-y-0 lg:space-x-0">
                {/* Top Card */}
                <div className="h-fit flex-1 p-4 sm:p-6 lg:p-9 bg-white rounded-[1.25rem] shadow-sm border border-light-blue min-h-[300px]">
                  {/* Header */}
                  <div className="flex flex-col gap-6 mb-10 ">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:gap-0">
                      <div className="flex flex-col">
                        <h2 className="text-primary mb-0 font-nonito_sans font-semibold text-[1.5rem] leading-[34px] lg:leading-[2.97rem]">
                          {orderData?.title}
                        </h2>
                        {/* Description */}
                        <p className="text-content text-sm font-inter max-w-[90%] mb-0 mt-2 sm:max-w-full">
                          {
                            orderStatusDescription[
                              overRideStatus(orderData, authData?.role)
                            ]?.[authData?.role]
                          }
                        </p>
                      </div>
                      <span className="text-dark font-nonito_sans font-semibold text-[1.5rem] leading-[2.97rem]">
                        {currencyFormatter.format(orderData?.price)}
                      </span>
                    </div>
                    {/* Task Details */}
                    <div>
                      {/* Mobile layout */}
                      <div className="flex flex-col gap-2 sm:hidden">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-dark font-inter">
                            Order ID
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`#${orderData?.id}`}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-dark font-inter">
                            Date
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {formatYYYY_MM_DD(orderData?.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-dark font-inter">
                            Service Pro's Name
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`${orderData?.tasker?.firstName} ${orderData?.tasker?.lastName}`}
                          </span>
                        </div>
                      </div>

                      {/* Desktop layout */}
                      <div className="hidden sm:flex gap-[2.125rem]">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-dark font-inter">
                            Order ID
                          </span>
                          <span className="text-sm text-dark font-inter">
                            Date
                          </span>
                          <span className="text-sm text-dark font-inter">
                            Service Pro's Name
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`#${orderData?.id}`}
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {formatYYYY_MM_DD(orderData?.createdAt)}
                          </span>
                          <span className="text-[#4A4A4A] text-sm font-inter">
                            {`${orderData?.tasker?.firstName} ${orderData?.tasker?.lastName}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {orderData?.status === orderStatus.ORDER_DELIVERED &&
                    Array.isArray(orderData?.user?.roles) &&
                    orderData?.user?.roles?.includes(roles.USER) && (
                      <div className="flex flex-col gap-4 ">
                        <CustomButton
                          onClick={handleAccept}
                          buttonText="Accept Order"
                          className="w-full text-base font-semibold text-white bg-primary font-inter"
                          disabled={isloading}
                          loading={isloading}
                        />

                        <CustomButton 
                          onClick={() =>
                            navigate(`/client/request-revision/${orderId}`)
                          }
                          buttonText="Request Revision"
                          className="w-full text-base font-semibold bg-white text-primary border-primary font-inter"
                          disabled={isloading}
                        />
                      </div>
                    )}

                  {orderData?.status === orderStatus.COMPLETED && (
                    <div className="flex flex-col gap-4 ">
                      {
                        <CustomButton
                          buttonText="Add Review"
                          onClick={() => {
                            if (!orderData?.userReview) {
                              setIsOpenReviewOrderModel(true);
                            } else {
                              showToast(
                                "error",
                                "User has already submitted a review"
                              );
                            }
                          }}
                          disabled={!!orderData?.userReview}
                          className="w-full text-base font-semibold text-white bg-primary font-inter"
                        />
                      }
                      <CustomButton
                        onClick={() =>
                          navigate(`/client/raise-complaint/${orderId}`)
                        }
                        buttonText="Raise Complaint"
                        className="w-full text-base font-semibold bg-white text-primary border-primary font-inter"
                      />
                    </div>
                  )}
                </div>

                {/* Progress Indicator */}
                <div className="block mt-6 lg:mt-0 lg:hidden md:h-full ">
                  <ProgressIndicator
                    orderFlow={orderData?.orderProgress || []}
                    revitions={orderData?.revisions || []}
                    orderData={orderData}
                  />
                </div>
              </div>

              {/* Bottom Card */}
              <div className="px-4 sm:px-6 pt-6 pb-12 bg-white rounded-[1.25rem] border border-light-blue">
                <h2 className="text-2xl font-semibold font-nonito_sans text-dark">
                  Order Details
                </h2>
                <div className="flex flex-col gap-6 ">
                  {/* Offer Details */}
                  {checkStatus(orderStatus.ORDER_IN_PROGRESS) && (
                    <OfferDetails
                      details={orderData}
                      showServiceProName={true}
                    />
                  )}

                  {/* Delivery Details */}
                  {checkStatus(orderStatus.ORDER_DELIVERED) && (
                    <DeliveryDetails details={orderData} />
                  )}
                  {/* { 
                            checkStatus(orderStatus.IN_REVISION) && 
                            <RevisionDetails details={orderData} />
                          } */}

                  {/* Revision , Delivered Details */}
                  {/* { 
                            checkStatus(orderStatus.IN_REVISION) &&  
                            checkStatus(orderStatus.IN_REVISION) && 
                            Array.isArray(orderData?.revisions) &&
                            orderData?.revisions.length > 0 &&
                            <DeliveryDetails details={orderData?.revisions.at(-1)} />
                          } */}

                  {checkStatus(orderStatus.IN_REVISION) &&
                    Array.isArray(orderData?.revisions) &&
                    orderData?.revisions.length > 0 &&
                    orderData?.revisions?.map((revision, index) => (
                      <>
                        <RevisionDetails details={revision} />

                        {revision.status === "COMPLETED" && (
                          <DeliveryDetails details={revision} />
                        )}
                      </>
                    ))}

                  {/* Complain Details */}
                  {checkStatus(orderStatus.COMPLAINT_RAISED) && (
                    <ComplainDetails details={orderData?.complaint} />
                  )}

                  {/* Resolved Details */}
                  {orderData?.status === orderStatus.COMPLAINT_RESOLVED && (
                    <ResolveDetails details={orderData?.complaint} />
                  )}
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="hidden mt-6 lg:mt-0 lg:block">
              <ProgressIndicator
                orderFlow={orderData?.orderProgress}
                revitions={orderData?.revisions || []}
                orderData={orderData}
              />
            </div>
          </div>
        )
      }

      {/* Accept Order Model  */}
      {confirmation?.isOpen && (
        <ConfirmationModal
          isOpen={confirmation?.isOpen}
          options={confirmation?.options}
          onConfirm={confirm}
          onCancel={cancel}
        />
      )}

      {/* Review Order Model */}
      <ReviewOrderModel
        isOpen={isOpenReviewOrderModel}
        setIsOpen={setIsOpenReviewOrderModel}
        onClose={() => setIsOpenReviewOrderModel(false)}
        onSubmit={handleOrderReviewSubmit}
        title="Review Order"
        loading={isAddOrderReview}
      />
    </div>
  );
};

export default OrderDetails;
